import React, { useState, useEffect } from 'react';
import { Package, ArrowRight, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
// Razorpay loaded via script tag in index.html
import axios from 'axios';
import './SamplePack.css';
import { samplePack } from '../mockData';
import { validatePincode } from '../utils/pincodeValidator';
import { useAuth } from '../context/AuthContext';
import SuccessModal from './SuccessModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SamplePack = () => {
  // Razorpay available via window.Razorpay
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [pincodeError, setPincodeError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
      const defaultAddr = user.addresses?.find(addr => addr.isDefault);
      if (defaultAddr) {
        setFormData(prev => ({
          ...prev,
          street: defaultAddr.street,
          city: defaultAddr.city,
          state: defaultAddr.state,
          pincode: defaultAddr.pincode
        }));
      }
    }
  }, [isAuthenticated, user]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'pincode' && value.length === 6) {
      const result = await validatePincode(value);
      if (!result.valid) {
        setPincodeError('Sorry, we don\'t deliver to this pincode yet.');
      } else {
        setPincodeError('');
      }
    } else if (name === 'pincode') {
      setPincodeError('');
    }
  };

  const handleOrderSample = async (e) => {
    e.preventDefault();
    
    const pincodeValidation = await validatePincode(formData.pincode);
    if (!pincodeValidation.valid) {
      setPincodeError('Sorry, we don\'t deliver to this pincode yet.');
      return;
    }
    
    setLoading(true);

    try {
      // Create order on backend
      const { data } = await axios.post(`${API}/orders/sample/create-order`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode
        }
      });

      // Razorpay options
      const options = {
        key: data.keyId,
        amount: data.amount,
        currency: 'INR',
        order_id: data.orderId,
        name: 'House of Microgreens',
        description: 'Sample Pack',
        handler: async (response) => {
          try {
            console.log('Sample pack payment successful, verifying...', response);
            // Verify payment on backend
            const verifyData = await axios.post(`${API}/orders/sample/verify-payment`, {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              customerDetails: formData
            }, {
              withCredentials: true
            });

            console.log('Sample pack verification successful:', verifyData);
            // Show success modal
            setShowSuccessModal(true);
            setShowForm(false);
            setFormData({ name: '', email: '', phone: '', street: '', city: '', state: '', pincode: '' });
          } catch (error) {
            console.error('Sample pack verification error:', error);
            console.error('Error response:', error.response);
            alert(`Payment verification failed: ${error.response?.data?.detail || error.message}. Please contact support.`);
          } finally {
            setLoading(false);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: '#8FEC78'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          }
        }
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    } catch (error) {
      setLoading(false);
      alert('Failed to create order. Please try again.');
      console.error(error);
    }
  };

  return (
    <section id="sample-pack" className="sample-pack-section">
      <div className="container">
        {!showForm ? (
          <div className="sample-pack-card">
            <div className="sample-pack-icon">
              <Package size={48} strokeWidth={1.5} />
            </div>
            <div className="sample-pack-content">
              <h3 className="heading-3">{samplePack.title}</h3>
              <p className="body-medium" style={{ marginTop: '0.75rem' }}>{samplePack.description}</p>
              <div className="sample-pack-price">
                <span className="price-label">One-time purchase:</span>
                <span className="price-value">₹{samplePack.price}</span>
              </div>
            </div>
            <button className="btn-primary" onClick={() => {
              setShowForm(true);
            }}>
              Order Sample Pack
              <ArrowRight size={20} />
            </button>
          </div>
        ) : (
          <div className="sample-pack-card form-card">
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>Enter Delivery Details</h3>
            {!isAuthenticated && (
              <div className="signin-note" data-testid="signin-note-sample">
                <AlertCircle size={16} />
                <span><button type="button" className="signin-link" onClick={() => navigate('/login', { state: { from: { pathname: '/#sample' } } })}>Sign in</button> to track your orders and manage cancellations</span>
              </div>
            )}
            <form onSubmit={handleOrderSample} className="sample-form">
              <div className="form-row">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                required
                className="form-input"
              />
              <input
                type="text"
                name="street"
                placeholder="Street Address"
                value={formData.street}
                onChange={handleChange}
                required
                className="form-input"
              />
              <div className="form-row">
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <input
                  type="text"
                  name="state"
                  placeholder="State"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>
              <input
                type="text"
                name="pincode"
                placeholder="Pincode"
                value={formData.pincode}
                onChange={handleChange}
                required
                className={`form-input ${pincodeError ? 'error' : ''}`}
                maxLength="6"
              />
              {pincodeError && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  <span>{pincodeError}</span>
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Processing...' : `Pay ₹${samplePack.price}`}
                  <ArrowRight size={20} />
                </button>
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
      
      {/* Success Modal */}
      <SuccessModal 
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
    </section>
  );
};

export default SamplePack;
